const ASPECT_RATIO_NAMES: Record<string, string> = {
  auto: "Auto",
  "1:1": "Square",
  "21:9": "Ultrawide",
  "8:1": "Panorama",
  "4:1": "Banner",
  "16:9": "Widescreen",
  "9:16": "Social story",
  "1:4": "Vertical banner",
  "1:8": "Vertical panorama",
  "4:3": "Classic",
  "4:5": "Social portrait",
  "5:4": "Landscape",
  "3:4": "Traditional",
  "3:2": "Standard",
  "2:3": "Portrait",
};

export function aspectRatioKey(ratio: string): string {
  return ratio.replace(":", "x");
}

export function aspectRatioName(ratio: string): string {
  return ASPECT_RATIO_NAMES[ratio] ?? "Custom";
}

export function aspectRatioPickerLabel(ratio: string): string {
  if (ratio === "auto") return aspectRatioName(ratio);
  return `${aspectRatioName(ratio)} · ${ratio}`;
}

export interface AspectRatioFrame {
  width: number;
  height: number;
}

/** Normalizes any valid W:H ratio into a compact, readable 16×16 frame. */
export function aspectRatioFrame(ratio: string): AspectRatioFrame | null {
  const [rawWidth, rawHeight, ...rest] = ratio.split(":");
  if (rest.length || !rawWidth || !rawHeight) return null;

  const width = Number(rawWidth);
  const height = Number(rawHeight);
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  if (width <= 0 || height <= 0) return null;

  const scale = 16 / Math.max(width, height);
  return {
    width: Math.max(3, roundFrameDimension(width * scale)),
    height: Math.max(3, roundFrameDimension(height * scale)),
  };
}

function roundFrameDimension(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Exact ratios supported by the creative model catalog, shared by browse filters. */
export const ASPECT_RATIOS = [
  "1:1", "4:3", "3:2", "16:9", "21:9", "20:9", "2:1", "4:1", "8:1",
  "5:4", "4:5", "3:4", "2:3", "9:16", "9:21", "9:20", "1:2", "1:4", "1:8",
  "210:297", "297:210",
] as const;

export function isAspectRatio(value: unknown): value is `${number}:${number}` {
  return typeof value === "string" && /^\d+(?:\.\d+)?:\d+(?:\.\d+)?$/.test(value) &&
    value.split(":").every(part => Number(part) > 0);
}
